<div class="pdf-preview-container">

  <ng-template #boundTableTpl let-tableData>
    <div class="bound-table-wrapper">
      <table class="w-full border-collapse bound-table">
        <colgroup>
          <col *ngFor="let width of tableData.columnWidthPercents" [style.width.%]="width">
        </colgroup>
        <thead>
          <tr>
            <th *ngFor="let field of tableData.fields" class="border border-gray-300 bg-gray-100 text-left">
              {{ field.name }}
            </th>
            <th *ngIf="tableData.hasRowTotals" class="border border-gray-300 bg-gray-100 text-center">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of tableData.rows">
            <td *ngFor="let field of tableData.fields" class="border border-gray-300 bound-table-cell">
              {{ row[field.uuid] || '' }}
            </td>
            <td *ngIf="tableData.hasRowTotals" class="border border-gray-300 bg-gray-50 text-center bound-table-cell">
              0
            </td>
          </tr>
        </tbody>
        <tfoot *ngIf="tableData.hasColumnTotals">
          <tr>
            <td *ngFor="let field of tableData.fields; let i = index"
                class="border border-gray-300 bg-gray-100 font-semibold bound-table-cell"
                [class.text-left]="i === 0">
              <ng-container *ngIf="i === 0">Total</ng-container>
              <ng-container *ngIf="i > 0 && field.enable_totals">0</ng-container>
            </td>
            <td *ngIf="tableData.hasRowTotals" class="border border-gray-300 bg-gray-100 text-center font-semibold bound-table-cell">
              0
            </td>
          </tr>
        </tfoot>
      </table>
      <div *ngIf="tableData.continuationLabel" class="bound-table-caption">
        {{ tableData.continuationLabel }}
      </div>
    </div>
  </ng-template>


  <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
    <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
    <h3 class="text-lg font-semibold text-gray-700 mb-2">Loading Preview</h3>
    <p class="text-sm text-gray-500">Please wait...</p>
  </div>


  <div *ngIf="error && !isLoading" class="flex flex-col items-center justify-center py-12">
    <lucide-icon name="alert-circle" [size]="48" class="text-red-500 mb-4"></lucide-icon>
    <h3 class="text-lg font-semibold text-gray-700 mb-2">Error Loading Preview</h3>
    <p class="text-sm text-red-600 mb-4">{{ error }}</p>
    <button
      class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      (click)="retry()">
      Retry
    </button>
  </div>


  <div *ngIf="!isLoading && !error && template && hasTagValueMap()" class="pdf-preview-content">
    <div class="pdf-content">

      <div *ngFor="let page of renderPages; trackBy: trackByPageUuid"
           class="pdf-page page-canvas"
           [style.width]="getPageWidth()"
           [style.height]="getPageHeight()">

        <div *ngIf="IS_DRAFT" class="watermark">
          {{ WATERMARK_TEXT }}
        </div>


        <div class="section-header"
             [style.height]="page.header.height ? page.header.height + 'mm' : '30mm'"
             [style.background-color]="page.header.backgroundColor || '#ffffff'">
          <div *ngFor="let element of page.header.elements; trackBy: trackByElementUuid"
               [class.element-container]="element.type !== 'line'"
               [class.element-container-line]="element.type === 'line'"
               [ngStyle]="getElementStyle(element)">
            <ng-container [ngSwitch]="element.type">

              <div *ngSwitchCase="'label'" class="element-label" [class.element-label-table]="isTableType(element)" [class.element-label-list]="isSlicedList(element)">
                <ng-container *ngIf="isTableType(element); else notTableHeader">
                  <ng-container *ngIf="getTableStructure(element) as tableData">
                    <ng-container *ngTemplateOutlet="boundTableTpl; context: { $implicit: tableData }"></ng-container>
                  </ng-container>
                </ng-container>
                <ng-template #notTableHeader>
                  <ng-container *ngIf="isListType(element); else normalTextHeader">
                    <div style="white-space: pre-line;">{{ getLabelText(element) }}</div>
                  </ng-container>
                  <ng-template #normalTextHeader>
                    {{ getLabelText(element) }}
                  </ng-template>
                </ng-template>
              </div>

              <div *ngSwitchCase="'image'" class="element-image">
                <img *ngIf="getImageSrc(element); else imagePlaceholder"
                     [src]="getImageSrc(element)"
                     class="w-full h-full object-contain"
                     alt="PDF Image" />
                <ng-template #imagePlaceholder>
                  <span class="text-gray-400 text-xs">Image</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'table_value'" class="element-table-value">
                <ng-container *ngIf="getTableData(element); else tablePlaceholder">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th *ngFor="let col of getTableData(element).definition">
                          {{ col.label || col.name }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of getTableData(element).data?.value?.rows">
                        <td *ngFor="let cell of row">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </ng-container>
                <ng-template #tablePlaceholder>
                  <span class="text-gray-400 text-xs">Table</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'shape'"
                   class="element-shape w-full h-full"
                   [style.backgroundColor]="$any(element).backgroundColor"
                   [style.border]="$any(element).borderWidth ? $any(element).borderWidth + 'px solid ' + $any(element).borderColor : 'none'"
                   [style.borderRadius.px]="$any(element).borderRadius || 0">
              </div>

              <div *ngSwitchCase="'line'"
                   class="element-line"
                   [style.backgroundColor]="$any(element).color">
              </div>
            </ng-container>
          </div>
        </div>


        <div class="section-body"
             [style.height]="'calc(' + getPageHeight() + ' - ' + (page.header.height || 30) + 'mm - ' + (page.footer.height || 20) + 'mm)'"
             [style.background-color]="page.body.backgroundColor || '#ffffff'">
          <div *ngFor="let element of page.body.elements; trackBy: trackByElementUuid"
               [class.element-container]="element.type !== 'line'"
               [class.element-container-line]="element.type === 'line'"
               [ngStyle]="getElementStyle(element)">
            <ng-container [ngSwitch]="element.type">

              <div *ngSwitchCase="'label'" class="element-label" [class.element-label-table]="isTableType(element)" [class.element-label-list]="isSlicedList(element)">
                <ng-container *ngIf="isTableType(element); else notTableBody">
                  <ng-container *ngIf="getTableStructure(element) as tableData">
                    <ng-container *ngTemplateOutlet="boundTableTpl; context: { $implicit: tableData }"></ng-container>
                  </ng-container>
                </ng-container>
                <ng-template #notTableBody>
                  <ng-container *ngIf="isListType(element); else normalTextBody">
                    <div style="white-space: pre-line;">{{ getLabelText(element) }}</div>
                  </ng-container>
                  <ng-template #normalTextBody>
                    {{ getLabelText(element) }}
                  </ng-template>
                </ng-template>
              </div>

              <div *ngSwitchCase="'image'" class="element-image">
                <img *ngIf="getImageSrc(element); else imagePlaceholder"
                     [src]="getImageSrc(element)"
                     class="w-full h-full object-contain"
                     alt="PDF Image" />
                <ng-template #imagePlaceholder>
                  <span class="text-gray-400 text-xs">Image</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'table_value'" class="element-table-value">
                <ng-container *ngIf="getTableData(element); else tablePlaceholder">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th *ngFor="let col of getTableData(element).definition">
                          {{ col.label || col.name }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of getTableData(element).data?.value?.rows">
                        <td *ngFor="let cell of row">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </ng-container>
                <ng-template #tablePlaceholder>
                  <span class="text-gray-400 text-xs">Table</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'shape'"
                   class="element-shape w-full h-full"
                   [style.backgroundColor]="$any(element).backgroundColor"
                   [style.border]="$any(element).borderWidth ? $any(element).borderWidth + 'px solid ' + $any(element).borderColor : 'none'"
                   [style.borderRadius.px]="$any(element).borderRadius || 0">
              </div>

              <div *ngSwitchCase="'line'"
                   class="element-line"
                   [style.backgroundColor]="$any(element).color">
              </div>
            </ng-container>
          </div>
        </div>


        <div class="section-footer"
             [style.height]="page.footer.height ? page.footer.height + 'mm' : '20mm'"
             [style.background-color]="page.footer.backgroundColor || '#ffffff'">
          <div *ngFor="let element of page.footer.elements; trackBy: trackByElementUuid"
               [class.element-container]="element.type !== 'line'"
               [class.element-container-line]="element.type === 'line'"
               [ngStyle]="getElementStyle(element)">
            <ng-container [ngSwitch]="element.type">
              <!-- Label -->
              <div *ngSwitchCase="'label'" class="element-label" [class.element-label-table]="isTableType(element)" [class.element-label-list]="isSlicedList(element)">
                <ng-container *ngIf="isTableType(element); else notTableFooter">
                  <ng-container *ngIf="getTableStructure(element) as tableData">
                    <ng-container *ngTemplateOutlet="boundTableTpl; context: { $implicit: tableData }"></ng-container>
                  </ng-container>
                </ng-container>
                <ng-template #notTableFooter>
                  <ng-container *ngIf="isListType(element); else normalTextFooter">
                    <div style="white-space: pre-line;">{{ getLabelText(element) }}</div>
                  </ng-container>
                  <ng-template #normalTextFooter>
                    {{ getLabelText(element) }}
                  </ng-template>
                </ng-template>
              </div>

              <div *ngSwitchCase="'image'" class="element-image">
                <img *ngIf="getImageSrc(element); else imagePlaceholder"
                     [src]="getImageSrc(element)"
                     class="w-full h-full object-contain"
                     alt="PDF Image" />
                <ng-template #imagePlaceholder>
                  <span class="text-gray-400 text-xs">Image</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'table_value'" class="element-table-value">
                <ng-container *ngIf="getTableData(element); else tablePlaceholder">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th *ngFor="let col of getTableData(element).definition">
                          {{ col.label || col.name }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of getTableData(element).data?.value?.rows">
                        <td *ngFor="let cell of row">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </ng-container>
                <ng-template #tablePlaceholder>
                  <span class="text-gray-400 text-xs">Table</span>
                </ng-template>
              </div>

              <div *ngSwitchCase="'shape'"
                   class="element-shape w-full h-full"
                   [style.backgroundColor]="$any(element).backgroundColor"
                   [style.border]="$any(element).borderWidth ? $any(element).borderWidth + 'px solid ' + $any(element).borderColor : 'none'"
                   [style.borderRadius.px]="$any(element).borderRadius || 0">
              </div>

              <div *ngSwitchCase="'line'"
                   class="element-line"
                   [style.backgroundColor]="$any(element).color">
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
